<script lang="ts" setup>
import { ref, onUnmounted, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { usePayrollChange } from '@/app/core/composable/routes/useChangePayroll'
import { getErrorCode } from '@/app/core/composable/general'
import { useI18n } from 'vue-i18n'
import Popper from 'vue3-popper'
import {
  DBtn,
  DTextField,
  DTable,
  DLoading,
  SearchMdIcon,
  XIcon,
  CheckIcon,
  Loading02Icon,
  FileCheck02Icon
} from 'front-library'
import {
  getTransferRequests,
  TransferRequest
} from '@/app/core/services/transfer-requests'
usePayrollChange(() => window.location.reload())

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Loading */
const loading = ref(false)

/* Datos */
const search = ref()
const tableTransferRequests = ref(null)
const refresh = ref()
const isRefreshData = ref<boolean>(false)

/* Datos componente table */
const columns = ref([
  { name: 'payrollOrigin', title: t('transfer.list.payrollOrigin')},
  { name: 'payrollDestination', title: t('transfer.list.payrollDestination')},
  {
    name: 'employeeOrigin',
    title: t('transfer.list.employee'),
    table: 'employees.name'
  },
  {
    name: 'statusProcess',
    title: t('transfer.list.status'),
    table: 'process_status.name'
  },
  {
    name: 'creationUser',
    title: t('transfer.list.creationUser'),
    table: 'transfer_requests.created_by'
  },
  {
    name: 'updated_at',
    title: t('transfer.list.lastUpdate'),
    table: 'transfer_requests.updated_at'
  },
  {
    name: 'closed_by',
    title: t('transfer.list.closedBy'),
    table: 'transfer_requests.closed_by'
  },
  { name: 'actions', title: '' }
])
const rows = ref<TransferRequest[]>([])
const perPage = ref(0)
const offset = ref(0)
const totalRows = ref(0)
const sortBy = ref('')

/* Redirect's */
const createTransferRequest = () => {
  void router.push({ name: 'CreateTransferRequest' })
}

const getTransferRequest = (id: any) => {
  void router.push({ name: 'GetTransferRequest', params: { id } })
}

/* Listar traslados */
const transfers = async (load: boolean = true) => {
  if (load) loading.value = true
  isRefreshData.value = true
  try {
    const data: any = await getTransferRequests(
      offset.value,
      perPage.value,
      sortBy.value,
      search.value === '' ? undefined : search.value
    )
    rows.value = data.results
    totalRows.value = data.totalItems
    loading.value = false
    isRefreshData.value = false
  } catch (error: any) {
    if (error.response?.data) {
      const code = getErrorCode(error.response.data?.code)
      notifications.addNotification({
        title: t('error.titles.' + code),
        text: t('error.messages.' + code),
        type: 'error',
        time: 10000
      })
    } else {
      notifications.addNotification({
        title: 'Error',
        text: error,
        type: 'error',
        time: 10000
      })
    }
    loading.value = false
    isRefreshData.value = false
  }
}

onMounted(() => {
  refresh.value = setInterval(() => {
    void transfers(false)
  }, 10000)
})

onUnmounted(() => {
  clearInterval(refresh.value)
})
</script>
<template>
  <DLoading v-show="loading" :message="t('loading.data')" />
  <div class="h-full px-5 overflow-auto scroll">
    <div class="bg-gray-50 pb-4">
      <div class="leading-4 pt-responsive">
        <p
          class="text-3xl font-semibold text-shadow"
          v-html="t('transfer.list.title', 1)" />
        <p
          class="text-gray-500 text-shadow"
          v-html="t('transfer.list.description')" />
      </div>
      <div class="sm:flex justify-between pt-8">
        <div class="flex mb-3 sm:mb-0 max-w-2xl sm:w-2/3 md:w-1/2 lg:w-5/12">
          <DTextField
            :placeholder="t('form.button.search') + '...'"
            :icon="'SearchLgIcon'"
            class="mr-2 w-full"
            v-model="search"
            @keyup.enter="transfers" />
          <DBtn @click.prevent="transfers" v-html="t('form.button.search')" />
        </div>
        <DBtn
          :icon="'PlusIcon'"
          type="button"
          @click="createTransferRequest()"
          v-if="store.hasPermission('transfer:create')">
          {{
            t('form.button.create', {
              form: t('transfer.list.title', 0)
            })
          }}
        </DBtn>
      </div>
    </div>
    <DTable
      ref="tableTransferRequests"
      :columns="columns"
      :rows="rows"
      :total-rows="totalRows"
      :select-options="false"
      :text-no-data="t('table.noData')"
      :text-per-page="t('table.rowsPerPage')"
      @perPage=";(perPage = $event), transfers()"
      @offset="offset = $event"
      @sort=";(sortBy = $event), transfers()">
      <template #actions="data: any">
        <div class="flex py-2.5 justify-end gap-1">
          <Popper
            v-if="store.hasPermission('transfer:create')"
            arrow
            offsetDistance="12"
            :content="t('table.action.detail')"
            :hover="true"
            placement="top"
            class="tooltip">
            <div
              class="bg-gray-50 rounded-md py-2"
              @click="getTransferRequest(data.id)">
              <SearchMdIcon class="h-6 w-6 mx-2 cursor-pointer text-gray-600" />
            </div>
          </Popper>
        </div>
      </template>
    </DTable>
  </div>
</template>
